import type { TrackDto, TrackSessionDto } from '../api/apiTypes'
import type { Lap } from '../domain/lap/Lap'
import type { TelemetryDiagnostics } from '../domain/telemetry/processed/TelemetryDiagnostics'
import { processTrackSessionWithTrackDto } from '../processors/pipeline'
import type {
  DataQualityMetrics,
  StageExecutionInfo,
} from '../processors/pipeline/ProcessingPipeline'
import type { Finding } from '../telemetryChecks/finding'
import { UnusableTelemetryError } from '../telemetryChecks/unusableTelemetryError'
import { lapFindingsOf } from '../telemetryChecks/lapChecks/lapReliability'
import { imuVibrationFinding } from '../telemetryChecks/streamChecks/imuVibration'

export interface SessionLaps {
  laps: Lap[]
  stages: StageExecutionInfo[]
  dataQuality: DataQualityMetrics
  diagnostics: TelemetryDiagnostics | null
  findings: Finding[]
}

const NO_DATA: DataQualityMetrics = {
  hasGps: false,
  hasGyroscope: false,
  hasAccelerometer: false,
  gpsPointCount: 0,
  averageSamplingFrequency: 0,
}

export async function processSessionLaps(
  sessionDto: TrackSessionDto,
  trackDto: TrackDto
): Promise<SessionLaps> {
  try {
    return await processedSessionLaps(sessionDto, trackDto)
  } catch (error) {
    if (!(error instanceof UnusableTelemetryError)) throw error
    return {
      laps: [],
      stages: error.facts?.stages ?? [],
      dataQuality: error.facts?.dataQuality ?? NO_DATA,
      diagnostics: null,
      findings: [...error.findings],
    }
  }
}

async function processedSessionLaps(
  sessionDto: TrackSessionDto,
  trackDto: TrackDto
): Promise<SessionLaps> {
  const hasFinishLine = trackDto.finishLine && trackDto.finishLine.length >= 1
  const result = await processTrackSessionWithTrackDto(
    sessionDto,
    trackDto,
    undefined,
    hasFinishLine ? { skipLapStages: true } : undefined
  )
  const trackSession = result.trackSession
  if (hasFinishLine) {
    trackSession.applyStartFinishForSession(
      0,
      trackDto.finishLine.map((c) => ({
        latitude: c.latitude,
        longitude: c.longitude,
      }))
    )
  }
  const { stages, dataQuality, findings } = result.processingMetadata
  const diagnostics = trackSession.processedTelemetry.diagnostics ?? null
  return {
    laps: trackSession.laps,
    stages,
    dataQuality,
    diagnostics,
    findings: [
      ...findings,
      ...lapFindingsOf(trackSession.laps),
      ...presentOnly(imuVibrationFinding(diagnostics)),
    ],
  }
}

const presentOnly = (finding: Finding | null): Finding[] =>
  finding === null ? [] : [finding]
